/**
 * Hydrates the Profile view from real account data.
 *
 * Historical bug: the previous implementation painted values with
 * `byId(root, "profile.displayName")`, which resolves an *element id*
 * (`#profile\.displayName`). The markup only carries `data-bind`
 * attributes, never those ids, so every `set()` silently no-opped and
 * a logged-in Max user still saw "Guest / Loading… / Free Plan / —".
 * This module now binds by the `[data-bind="<path>"]` selector (the
 * same convention the rest of the prototype uses) and additionally
 * drives the subscription card's free-vs-paid state and billing action
 * row so the page is a real closed loop.
 *
 * Four sources are merged:
 *   1. /api/account/dashboard   — user + subscription summary + history
 *   2. /api/user/learning-stats — topicsExplored, lessonsCompleted, articlesRead, audioListened, videosWatched, daysActive
 *   3. /api/subscription/current — authoritative cancelAtPeriodEnd /
 *                                   billingCycle / provider ids for the
 *                                   billing-management controls
 *   4. /api/billing/quota-usage — authoritative AI Credits balance
 */
import { byId } from "./domUtils";
import type { Root } from "./prototypeTypes";
import type { PrototypeApiClient } from "./apiClient";
import type { AccountDashboardResponse, LearningStatsResponse } from "./apiClientResponses";
import { isPaidPlan } from "./controllerState";
import { planBadgeHtml, resolveDarkTheme, type PlanTier } from './planBadge';
import { paintProfileHistory } from "./profileHistoryRenderer";
import { renderEmptyStateMarkup } from './emptyStateMarkup';
import { accessAfterTemporaryTier, type ClientBillingPlan } from './clientPlanContext';

type DashboardSub = AccountDashboardResponse["subscription"] | null;
type CurrentSub = Awaited<ReturnType<PrototypeApiClient["getCurrentSubscription"]>>["subscription"];
type PlanContext = AccountDashboardResponse['planContext'];
type QuotaSummary = Awaited<ReturnType<PrototypeApiClient['getQuotaUsage']>>['summary'];

// A Profile root is reused across sign-in, sign-out, and account switches.
// Network responses from the old viewer can arrive after the new viewer has
// already hydrated the same DOM, so each run owns a monotonic generation.
const hydrationGenerations = new WeakMap<object, number>();

function beginHydration(root: Root): number {
	const generation = (hydrationGenerations.get(root) ?? 0) + 1;
	hydrationGenerations.set(root, generation);
	return generation;
}

function isCurrentHydration(root: Root, generation: number): boolean {
	return hydrationGenerations.get(root) === generation;
}

export async function hydrateProfile(
  root: Root,
  api: PrototypeApiClient,
  viewer?: { isLoggedIn: boolean },
): Promise<void> {
	const generation = beginHydration(root);
  if (viewer?.isLoggedIn === false) {
    paintVisitorProfile(root);
		setProfileBusy(root, false);
    return;
  }
  const [dashRes, statsRes, subRes, quotaRes] = await Promise.allSettled([
    api.getAccountDashboard(),
    api.getLearningStats(),
    api.getCurrentSubscription(),
		typeof api.getQuotaUsage === 'function' ? api.getQuotaUsage(1) : Promise.reject(new Error('quota_usage_unavailable')),
  ]);
  const dashboard = dashRes.status === "fulfilled" ? dashRes.value : null;
  const stats = statsRes.status === "fulfilled" ? statsRes.value : null;
  const current = subRes.status === "fulfilled" ? subRes.value.subscription : null;
	const dashboardSubscription = dashboard?.subscription && ['active', 'past_due'].includes(dashboard.subscription.status)
		? dashboard.subscription
		: null;

  const hasVisitorDashboard = dashRes.status === "fulfilled" && dashRes.value.user === null;
  if (hasVisitorDashboard || [dashRes, statsRes, subRes].every(isUnauthorizedResult)) {
		if (!isCurrentHydration(root, generation)) return;
    paintVisitorProfile(root);
		setProfileBusy(root, false);
    return;
  }
	if (!isCurrentHydration(root, generation)) return;

	const billingPlan = dashboard?.planContext?.billingPlan ?? (current?.plan ?? dashboardSubscription?.plan ?? 'free').toLowerCase();
	const effectivePlan = dashboard?.planContext?.effectiveAccessPlan ?? billingPlan;
	if (dashboard?.user) paintUser(root, dashboard.user, effectivePlan);
  paintStats(root, stats);
	// A dashboard transport failure is unknown data, not an empty learning
	// history. Keep those user-facing states distinguishable; a later Profile
	// hydration retries the dashboard request and replaces this state normally.
  paintProfileHistory(root, dashboard?.history, dashRes.status !== 'fulfilled');
	paintAccessSummary(root, dashboardSubscription, current, dashboard?.planContext ?? null, quotaRes.status === 'fulfilled' ? quotaRes.value.summary : null);
	paintPasswordSecurity(root, dashboard?.user?.hasPassword ?? false);
	setProfileBusy(root, false);
  // Fetch categories sorted by engagement. An obsolete account response must
  // never repaint the current viewer's category profile.
  void paintCategoryInterests(root, api, () => isCurrentHydration(root, generation));
}

export function primeProfileFirstFrame(
	root: Root,
	viewer: { isLoggedIn: boolean; plan: string; avatarInitials: string },
): void {
	if (!viewer.isLoggedIn) return;
	bind(root, 'profile.avatar', viewer.avatarInitials || '·');
	paintPlanBadge(root, planTierFor(viewer.plan), viewer.plan === 'visitor' ? 'free' : viewer.plan);
	const signIn = byId(root, 'profileSigninCta');
	if (signIn) signIn.style.display = 'none';
}

/** 五档身份徽章：plan 字符串 → PlanTier（visitor 归 guest）。 */
function planTierFor(plan: string): PlanTier {
	return plan === 'lite' || plan === 'pro' || plan === 'max' || plan === 'free' ? plan : 'guest';
}

/** 把 profile.planBadge 渲染成内联样式的新徽章（planBadge.ts 唯一真相源）。 */
function paintPlanBadge(root: Root, tier: PlanTier, dataPlan: string): void {
	const badge = root.querySelector<HTMLElement>('[data-bind="profile.planBadge"]');
	if (!badge) return;
	badge.innerHTML = planBadgeHtml(tier, resolveDarkTheme());
	badge.setAttribute('data-plan', dataPlan);
	badge.removeAttribute('data-proto-click');
}

function setProfileBusy(root: Root, busy: boolean): void {
	byId(root, 'profileIdentityPanel')?.setAttribute('aria-busy', String(busy));
}

function paintUser(root: Root, user: AccountDashboardResponse["user"], plan: string): void {
  // Display names render exactly as stored at registration/creation; never
  // mutate them for display (the plan is shown separately via the badge).
  const displayName = user.displayName || user.email.split("@")[0];
  const initials = displayName
    .split(/[\s@.]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("");
  bind(root, "profile.avatar", initials || "·");
  bind(root, "profile.displayName", displayName);
  bind(root, "profile.identity", user.email);
  const signIn = byId(root, "profileSigninCta");
  if (signIn) signIn.style.display = "none";

  // Plan badge — the inline-styled five-tier identity badge
  paintPlanBadge(root, planTierFor(plan), plan === "visitor" ? "free" : plan);
}

function paintPasswordSecurity(root: Root, hasPassword: boolean): void {
	const row = byId(root, 'profilePasswordRow');
	const button = byId(root, 'profilePasswordAction') as HTMLButtonElement | null;
	if (row) {
		row.hidden = false;
		row.style.display = '';
	}
	if (!button) return;
	button.dataset.hasPassword = hasPassword ? '1' : '0';
	button.textContent = hasPassword ? 'Change password' : 'Set password';
}

function paintVisitorProfile(root: Root): void {
  const avatarEl = root.querySelector<HTMLElement>('.profile-avatar');
  if (avatarEl) {
    avatarEl.innerHTML = '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>';
  }
  bind(root, "profile.displayName", "Guest");
  bind(root, "profile.identity", "Sign in to view your learning history and subscription.");
  paintStats(root, null);
  paintProfileHistory(root, undefined);
	bind(root, 'profile.accessSummary', 'Sign in to view access and AI Credits');
	resetAutoRenewSummary(root);
	const actions = byId(root, 'subActions');
	if (actions) actions.style.display = 'none';
	const badge = root.querySelector<HTMLElement>('[data-bind="profile.planBadge"]');
	if (badge) paintPlanBadge(root, 'guest', 'free');
  const signIn = byId(root, "profileSigninCta");
  if (signIn) signIn.style.display = "";
	const passwordRow = byId(root, 'profilePasswordRow');
	if (passwordRow) {
		passwordRow.hidden = true;
		passwordRow.style.display = 'none';
	}
  const interests = root.querySelector<HTMLElement>('[data-bind="profile.interests"]');
  if (interests) {
    const message = document.createElement("div");
    message.style.cssText = "text-align:center;padding:16px;";
    message.innerHTML = '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--fg-tertiary)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="margin:0 auto 8px;display:block;opacity:0.6;"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg><p style="font-size:13px;color:var(--fg-tertiary);margin:0;">Sign in to build a personalized topic profile.</p>';
    interests.replaceChildren(message);
  }
}

function isUnauthorizedResult(result: PromiseSettledResult<unknown>): boolean {
  return result.status === "rejected"
    && typeof result.reason === "object"
    && result.reason !== null
    && "status" in result.reason
    && result.reason.status === 401;
}

/** All six stat cards show real account data from /api/user/learning-stats. */
function paintStats(root: Root, stats: LearningStatsResponse | null): void {
  bind(root, "profile.topicsExplored", String(stats?.topicsExplored ?? 0));
  bind(root, "profile.chaptersDone", String(stats?.lessonsCompleted ?? 0));
  bind(root, "profile.articlesRead", String(stats?.articlesRead ?? 0));
  bind(root, "profile.audioListened", String(stats?.audioListened ?? 0));
  bind(root, "profile.videosWatched", String(stats?.videosWatched ?? 0));
  bind(root, "profile.daysActive", String(stats?.daysActive ?? 0));
}

function paintAccessSummary(root: Root, dashboardSub: DashboardSub, current: CurrentSub, planContext: PlanContext | null, quota: QuotaSummary | null): void {
	const actions = byId(root, 'subActions');
	if (actions) actions.style.display = '';
	resetAutoRenewSummary(root);

	const billingPlan = normalizePlan(planContext?.billingPlan ?? current?.plan ?? dashboardSub?.plan ?? 'free');
	const promotionalPlan = planContext?.promotionalAccessPlan ?? null;
	const temporary = planContext?.temporaryAccess ?? null;
	const isPaid = isPaidPlan(billingPlan as Parameters<typeof isPaidPlan>[0]);
	const credits = quota ? `${quota.available} AI Credits` : 'AI Credits unavailable';

	if (temporary && promotionalPlan && planContext) {
		const pass = `${capitalize(promotionalPlan)} Pass through ${formatCompactDate(temporary.currentTierEndsAt)}`;
		const nextPlan = accessAfterTemporaryTier(planContext);
		const summary = isPaid
			? planRank(promotionalPlan) > planRank(billingPlan)
				? `Paid ${capitalize(billingPlan)} · Redeemed ${pass} · Then ${capitalize(nextPlan)} · ${credits}`
				: `Paid ${capitalize(billingPlan)} · Redeemed ${pass} · ${capitalize(billingPlan)} stays active · ${credits}`
			: `${pass} · Then ${capitalize(nextPlan)} · ${credits}`;
		bind(root, 'profile.accessSummary', summary);
		return;
	}

	if (isPaid && current?.cancelAtPeriodEnd) {
		bind(root, 'profile.accessSummary', `${capitalize(billingPlan)} access through ${formatCompactDate(current.currentPeriodEnd)} ·`);
		const resume = byId(root, 'profileAutoRenewResume');
		if (resume) {
			resume.style.display = '';
			resume.textContent = 'Auto-renewal off · Turn on';
			resume.setAttribute('data-proto-click', 'resumeAutoRenew()');
		}
		const suffix = byId(root, 'profileAccessSummarySuffix');
		if (suffix) {
			suffix.style.display = '';
			suffix.textContent = `· ${credits}`;
		}
		return;
	}

	bind(root, 'profile.accessSummary', `${capitalize(billingPlan)} plan · ${credits}`);
}

function resetAutoRenewSummary(root: Root): void {
	const resume = byId(root, 'profileAutoRenewResume');
	if (resume) {
		resume.style.display = 'none';
		resume.removeAttribute('data-proto-click');
	}
	const suffix = byId(root, 'profileAccessSummarySuffix');
	if (suffix) {
		suffix.style.display = 'none';
		suffix.textContent = '';
	}
}

function normalizePlan(value: string): ClientBillingPlan {
	return value === 'lite' || value === 'pro' || value === 'max' ? value : 'free';
}

function planRank(plan: ClientBillingPlan): number {
	return { free: 0, lite: 1, pro: 2, max: 3 }[plan];
}

function bind(root: Root, path: string, value: string): void {
  root.querySelectorAll<HTMLElement>(`[data-bind="${path}"]`).forEach((node) => {
    node.textContent = value;
  });
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatDate(iso: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatCompactDate(iso: string | null): string {
	if (!iso) return '—';
	const date = new Date(iso);
	if (!Number.isFinite(date.getTime())) return iso;
	// Temporary access end times are an entitlement contract, not a viewer-local
	// deadline.  Rendering the instant in the browser timezone moves midnight UTC
	// grants to the prior calendar day for North American users (for example,
	// 2026-08-15T00:00:00Z became “Aug 14” in Los Angeles).
	return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** Fetch categories sorted by user engagement and render them. */
async function paintCategoryInterests(root: Root, api: PrototypeApiClient, isCurrent: () => boolean): Promise<void> {
  try {
    const res = await api.getCategoryInterests();
		if (!isCurrent()) return;
    const container = root.querySelector<HTMLElement>('[data-bind="profile.interests"]');
    if (!container) return;
    if (res.categories.length === 0) {
      // Insert the empty-state host directly into the flex container. Wrapping
      // it in an anonymous div makes the host's width:100% / flex:1 1 100%
      // resolve against that content-sized wrapper instead of the flex line,
      // collapsing the empty state to a small left-aligned block.
      const frag = document.createElement("div");
      frag.innerHTML = renderEmptyStateMarkup({ icon: 'book', title: 'No categories yet', subtitle: 'Start exploring topics to see your interests here.' });
      container.replaceChildren(...frag.children);
      return;
    }
    container.replaceChildren(
      ...res.categories.map((cat) => {
        const pill = document.createElement('button');
		pill.type = 'button';
        pill.className = "pill pill-category";
        pill.textContent = cat.label;
        pill.setAttribute("data-proto-click", `activateBrowseFilter('${cat.slug}');return false;`);
		pill.setAttribute('aria-label', `Browse ${cat.label} topics`);
        pill.style.cursor = "pointer";
        return pill;
      }),
    );
  } catch {
    // Fallback: leave existing category buttons as-is
  }
}
